import cn from 'classnames';
import Image from 'next/image';
import type { FC } from 'react';
import React from 'react';

import ALink from 'components/basic/ALink';
import RainbowHighlight from 'components/basic/RainbowHighlight';
import Typograf from 'components/basic/Typograf';
import { BodyL, H1, H2, H3 } from 'components/basic/Typography';
import { typeformUrl } from 'components/pagesComponents/landing/ForResearches/constants';

import s from './Databases.module.scss';

const Databases: FC = () => {
  return (
    <Typograf>
      <RainbowHighlight
        className="mb-lg-20 mb-xm-16 mb-sm-23  mt-xl-25 mt-lg-22 mt-xm-20 mt-sm-23"
        text="One of the largest HRV databases in the world"
        buttonText="Apply now"
        href={typeformUrl}
      />
      <div className={s.databases}>
        <Image
          width={1217}
          height={506}
          src="/pagesImages/landing/for-researches/databases/databases.jpg"
          layout="intrinsic"
        />
      </div>
      <H1
        className="mt-xl-10 mt-lg-6 mt-xm-10 mt-sm-6 mb-md-6 mb-sm-4"
        Tag="h2"
      >
        {`Cases of how do global universities use &#10;the Welltory app in their studies`}
      </H1>
      <H3 className={cn('mb-sm-18 mb-xm-12 mb-lg-10', s.h3)} Tag="h3">
        Welltory is an acknowledged app in scientific research, used at dozens
        of universities around the globe. We employ the full range of HRV
        measurements to collect accurate, large insights both qualitatively and
        quantitatively.
      </H3>
      <H2 className={cn(s.h2)} Tag="h4">
        {/* @ts-ignore */}
        <a name="How-do-universities-use- the-Welltory-app">
          As a stress detection and management app
        </a>
      </H2>
      <hr className={cn(s.hr, 'mt-sm-4 mt-xl-6 mb-sm-8')} />

      <div className="grid">
        <div
          className={cn(
            s.university,
            'g-col-md-6 g-col-sm-12 g-col-xm-4 g-col-lg-4 mb-sm-8 mb-xm-12 mb-lg-11 mb-xl-16',
          )}
        >
          <Image
            width={280}
            height={82}
            src="/pagesImages/landing/for-researches/databases/ucl.jpg"
            layout="intrinsic"
          />
          <BodyL className="mt-sm-5 mt-xl-7">
            <ALink
              href="https://uclic.ucl.ac.uk/content/2-study/4-current-taught-course/1-distinction-projects/13-19/schmid_annika_2019.pdf"
              target="_blank"
            >
              Twenty participants were conducted to study
            </ALink>{' '}
            the effects of action planning in personal informatics with the
            example of a stress management app.
          </BodyL>
        </div>
        <div
          className={cn(
            s.university,
            'g-col-md-6 g-col-sm-12 g-col-xm-4 g-col-lg-4 mb-sm-8 mb-xm-12 mb-lg-11 mb-xl-16',
          )}
        >
          <Image
            width={279}
            height={82}
            src="/pagesImages/landing/for-researches/databases/texas.jpg"
            layout="intrinsic"
          />
          <BodyL className="mt-sm-5 mt-xl-7">
            <ALink
              href="https://pubmed.ncbi.nlm.nih.gov/33346600/"
              target="_blank"
            >
              To enhance empirical evidence supporting
            </ALink>{' '}
            the research on a campus therapy dog's aid by explaining the dog's
            effect on effect on nursing student stress.
          </BodyL>
        </div>

        <div
          className={cn(
            s.university,
            'g-col-md-6 g-col-sm-12 g-col-xm-4 g-col-lg-4 mb-sm-8 mb-xm-12 mb-lg-11 mb-xl-16',
          )}
        >
          <Image
            width={382}
            height={82}
            src="/pagesImages/landing/for-researches/databases/asu.jpg"
            layout="intrinsic"
          />
          <BodyL className="mt-sm-5 mt-xl-7">
            <ALink
              href="https://www.sciencedirect.com/science/article/abs/pii/S0197457221000641"
              target="_blank"
            >
              In a randomized controlled pilot study
            </ALink>{' '}
            among ten informal Alzheimer's disease caregivers, we examined a
            two-week ten-minute daily, internet-delivered heart-focused
            breathing protocol compared to a waitlist control.
          </BodyL>
        </div>
      </div>
      <div className="grid">
        <div
          className={cn(
            s.university,
            'g-col-md-6 g-col-sm-12 g-col-xm-4 g-col-lg-4 mt-sm-18 mt-xm-12 mt-xl-16',
          )}
        >
          <H2>As an HRV monitor in a high-performance sports research group</H2>
          <hr className={cn(s.hr, 'mt-sm-4 mt-xl-6 mb-sm-8')} />

          <div />
          <Image
            width={295}
            height={84}
            src="/pagesImages/landing/for-researches/databases/mh.jpg"
            layout="intrinsic"
          />
          <BodyL className="mt-sm-5 mt-xl-7">
            <ALink
              href="https://www.sciencedirect.com/science/article/pii/S0169260722000815?via%3Dihub#"
              target="_blank"
            >
              Twenty-six professional cyclists in a resting state were measured
            </ALink>{' '}
            in supine and in seated positions through the simultaneous use of an
            electrocardiogram and two different smartphone applications that
            implement different technologies to measure HRV: Elite HRV (with a
            chest strap) and Welltory (photoplethysmography).
          </BodyL>
        </div>
        <div
          className={cn(
            s.university,
            'g-col-md-6 g-col-sm-12 g-col-xm-4 g-col-lg-4 mt-sm-18 mt-xm-12 mt-xl-16',
          )}
        >
          <H2> As an HRV monitor for early prediction of COVID-19</H2>
          <hr className={cn(s.hr, 'mt-sm-4 mt-xl-6 mb-sm-8')} />

          <div />
          <Image
            width={307}
            height={84}
            src="/pagesImages/landing/for-researches/databases/udc.jpg"
            layout="intrinsic"
          />
          <BodyL className="mt-sm-5 mt-xl-7">
            <ALink
              href="https://www.mdpi.com/1424-8220/21/24/8424/htm"
              target="_blank"
            >
              One hundred eighty-six patients with more than, 3200
            </ALink>{' '}
            HRV readings and numerous user textual logs were analyzed by the AI
            framework to generate the final decision as either "potentially
            COVID-19 infected" or "no evident signs of infection".
          </BodyL>
        </div>
      </div>
    </Typograf>
  );
};

export default Databases;
